export class AuthService {
  private static TOKEN_KEY = 'authToken';
  private static USERNAME_KEY = 'username';
  private static NICKNAME_KEY = 'nickname';
  private static EXPIRY_KEY = 'tokenExpiry';
  private static IS_ADMIN_KEY = 'isAdmin';
  private static IS_SUPER_USER_KEY = 'isSuperUser';

  /**
   * Check if user is authenticated
   */
  static isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) return false;

    // Check if token is expired
    const expiry = localStorage.getItem(this.EXPIRY_KEY);
    if (expiry && new Date() > new Date(expiry)) {
      this.logout();
      return false;
    }

    return true;
  }

  /**
   * Get the current auth token
   */
  static getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  /**
   * Get the current username
   */
  static getUsername(): string | null {
    return localStorage.getItem(this.USERNAME_KEY);
  }

  /**
   * Store authentication data
   */
  static getNickname(): string | null {
    return localStorage.getItem(this.NICKNAME_KEY);
  }

  static setAuthData(token: string, username: string, expiresAt?: string, isAdmin?: boolean, nickname?: string, isSuperUser?: boolean): void {
    localStorage.setItem(this.TOKEN_KEY, token);
    localStorage.setItem(this.USERNAME_KEY, username);
    if (nickname) {
      localStorage.setItem(this.NICKNAME_KEY, nickname);
    }
    if (expiresAt) {
      localStorage.setItem(this.EXPIRY_KEY, expiresAt);
    }
    localStorage.setItem(this.IS_ADMIN_KEY, (isAdmin ?? false).toString());
    localStorage.setItem(this.IS_SUPER_USER_KEY, (isSuperUser ?? false).toString());
  }

  /**
   * Check if current user is admin
   */
  static isAdmin(): boolean {
    return localStorage.getItem(this.IS_ADMIN_KEY) === 'true';
  }

  /**
   * Check if current user is super user
   */
  static isSuperUser(): boolean {
    return localStorage.getItem(this.IS_SUPER_USER_KEY) === 'true';
  }

  /**
   * Clear authentication data
   */
  static logout(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USERNAME_KEY);
    localStorage.removeItem(this.NICKNAME_KEY);
    localStorage.removeItem(this.EXPIRY_KEY);
    localStorage.removeItem(this.IS_ADMIN_KEY);
    localStorage.removeItem(this.IS_SUPER_USER_KEY);
  }

  /**
   * Get authorization header for API requests
   */
  static getAuthHeader(): Record<string, string> {
    const token = this.getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  /**
   * Check if token is expiring soon (within 5 minutes)
   */
  static isTokenExpiringSoon(): boolean {
    const expiry = localStorage.getItem(this.EXPIRY_KEY);
    if (!expiry) return false;

    const expiryDate = new Date(expiry);
    const fiveMinutesFromNow = new Date(Date.now() + 5 * 60 * 1000);
    
    return expiryDate < fiveMinutesFromNow;
  }

  /**
   * Parse JWT token to get payload (for debugging/display purposes)
   */
  static parseToken(): any {
    const token = this.getToken();
    if (!token) return null;

    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error parsing token:', error);
      return null;
    }
  }
}
